
let audioCtx = null

const getCtx = () => {
  if (typeof window === "undefined") return null
  const Ctor = window.AudioContext || window.webkitAudioContext
  if (!Ctor) return null
  if (!audioCtx) audioCtx = new Ctor()
  return audioCtx
}

export const unlockAudio = () => {
  const ctx = getCtx()
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {})
}

export const SOUND_PRESETS = {
  soft: {
    labelKey: "admin.sound_preset_soft",
    label: "Мягкий",
    notes: [
      { freq: 660, start: 0, dur: 0.18, type: "sine" },
      { freq: 880, start: 0.12, dur: 0.3, type: "sine" },
    ],
  },
  bell: {
    labelKey: "admin.sound_preset_bell",
    label: "Колокольчик",
    notes: [
      { freq: 1046, start: 0, dur: 0.7, type: "sine", gain: 0.9 },
      { freq: 2093, start: 0, dur: 0.45, type: "sine", gain: 0.25 },
    ],
  },
  drop: {
    labelKey: "admin.sound_preset_drop",
    label: "Капля",
    notes: [{ freq: 900, start: 0, dur: 0.16, type: "sine", slideTo: 450 }],
  },
  double: {
    labelKey: "admin.sound_preset_double",
    label: "Двойной",
    notes: [
      { freq: 740, start: 0, dur: 0.12, type: "triangle" },
      { freq: 740, start: 0.18, dur: 0.14, type: "triangle" },
    ],
  },
  alert: {
    labelKey: "admin.sound_preset_alert",
    label: "Сигнал",
    notes: [
      { freq: 520, start: 0, dur: 0.16, type: "square", gain: 0.3 },
      { freq: 780, start: 0.2, dur: 0.22, type: "square", gain: 0.3 },
    ],
  },
}

export const DEFAULT_SOUND_ID = "soft"

// ---------- id звуков компании ----------

const CUSTOM_ID_RE = /^custom:\d+$/

export const isCustomSoundId = (id) => typeof id === "string" && CUSTOM_ID_RE.test(id)
export const customSoundId = (dbId) => `custom:${dbId}`

// ---------- базовые пресеты ----------

export const playSound = (soundId, volume = 0.6) => {
  const preset = SOUND_PRESETS[soundId] ?? SOUND_PRESETS[DEFAULT_SOUND_ID]
  const ctx = getCtx()
  if (!ctx) return

  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {})

    const master = Math.min(1, Math.max(0, Number(volume) || 0))
    if (master === 0) return

    const now = ctx.currentTime

    preset.notes.forEach((note) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      const peak = 0.4 * master * (note.gain ?? 1)
      const t0 = now + note.start
      const t1 = t0 + note.dur

      osc.type = note.type ?? "sine"
      osc.frequency.setValueAtTime(note.freq, t0)
      if (note.slideTo) osc.frequency.exponentialRampToValueAtTime(note.slideTo, t1)

      gain.gain.setValueAtTime(0.0001, t0)
      gain.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t0 + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, t1)

      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(t0)
      osc.stop(t1 + 0.05)
    })
  } catch (_e) {
    // звук — не критичная функция, ошибки глотаем
  }
}

// ---------- загруженные звуки (AudioBuffer) ----------

// Загруженный файл может быть любой длины в рамках лимита размера,
// поэтому уведомление обрезаем, чтобы оно не играло минутами.
const MAX_CUSTOM_PLAY_SECONDS = 10

const bufferCache = new Map() // url -> Promise<AudioBuffer | null>

export const preloadSoundUrl = (url) => {
  if (!url) return Promise.resolve(null)

  if (!bufferCache.has(url)) {
    const promise = (async () => {
      const ctx = getCtx()
      if (!ctx) return null
      const res = await fetch(url, { credentials: "same-origin" })
      if (!res.ok) throw new Error(`sound fetch failed: ${res.status}`)
      const data = await res.arrayBuffer()
      // callback-форма — чтобы работало и в старых Safari
      return await new Promise((resolve, reject) => ctx.decodeAudioData(data, resolve, reject))
    })().catch(() => {
      bufferCache.delete(url) // дадим шанс повторить при следующем воспроизведении
      return null
    })
    bufferCache.set(url, promise)
  }

  return bufferCache.get(url)
}

export const forgetSoundUrl = (url) => {
  bufferCache.delete(url)
}

const playUrl = async (url, volume, onFail) => {
  const ctx = getCtx()
  const buffer = ctx ? await preloadSoundUrl(url) : null

  if (!ctx || !buffer) {
    onFail?.()
    return
  }

  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {})

    const master = Math.min(1, Math.max(0, Number(volume) || 0))
    if (master === 0) return

    const source = ctx.createBufferSource()
    const gain = ctx.createGain()
    source.buffer = buffer
    gain.gain.value = master

    source.connect(gain)
    gain.connect(ctx.destination)
    source.start()
    source.stop(ctx.currentTime + MAX_CUSTOM_PLAY_SECONDS)
  } catch (_e) {
    onFail?.()
  }
}

/**
 * Единая точка воспроизведения: базовый пресет или звук компании.
 * Если звук компании удалён или не загрузился — играем звук по умолчанию.
 */
export const playSoundById = (soundId, volume = 0.6, customSounds = []) => {
  if (isCustomSoundId(soundId)) {
    const sound = customSounds.find((s) => customSoundId(s.id) === soundId)
    const fallback = () => playSound(DEFAULT_SOUND_ID, volume)

    if (!sound) {
      fallback()
      return
    }

    playUrl(sound.url, volume, fallback)
    return
  }

  playSound(soundId, volume)
}
